import { useMemo } from 'react'
import { useRosterByEmailQuery } from '../../api/user-invitation-api-client'
import { useFundingInviteAccess } from '../UserManagement/useFundingInviteAccess'
import { joinNames } from '../UserManagement/userDisplay'
import {
  describeRecipientRemoval,
  type RecipientRemovalLine,
  type RemovedCompany,
} from './recipientRemoval'
import type { ReviewRecipientRow } from './hubspotSync.helpers'

const key = (email: string): string => email.trim().toLowerCase()

/**
 * The lines for a HubSpot review removal, one per person however many of
 * their companies are going, and whose company access comes off with it.
 */
export const useHubspotReviewRemoval = (rows: ReviewRecipientRow[]) => {
  const inviteAccess = useFundingInviteAccess()

  const byEmail = useMemo(() => {
    const grouped = new Map<
      string,
      { email: string; companies: RemovedCompany[] }
    >()
    for (const row of rows) {
      const entry = grouped.get(key(row.email)) ?? {
        email: row.email,
        companies: [],
      }
      entry.companies.push({
        clientPublicKey: row.clientPublicKey,
        companyName: row.companyName,
      })
      grouped.set(key(row.email), entry)
    }
    return grouped
  }, [rows])

  const emails = useMemo(
    () => Array.from(byEmail.values()).map((entry) => entry.email),
    [byEmail]
  )
  const { data, isLoading } = useRosterByEmailQuery(emails, {
    enabled: emails.length > 0,
  })

  return useMemo(() => {
    const rowByEmail = new Map(
      (data?.data ?? []).map((row) => [key(row.email), row])
    )
    const lines: RecipientRemovalLine[] = []
    const revokeEmails = new Set<string>()
    for (const [email, entry] of Array.from(byEmail.entries())) {
      const { text, revokes, kind } = describeRecipientRemoval(
        rowByEmail.get(email),
        entry.companies,
        inviteAccess.allowed
      )
      lines.push({
        email: entry.email,
        text,
        kind,
        companyNames: joinNames(
          entry.companies.map((company) => company.companyName)
        ),
      })
      if (revokes) revokeEmails.add(email)
    }
    return {
      lines,
      /** Lower-cased addresses whose company access comes off. */
      revokeEmails,
      canRevoke: inviteAccess.allowed,
      /** Still loading, so the lines are not final and confirming waits. */
      isChecking: emails.length > 0 && isLoading,
    }
  }, [data, byEmail, emails.length, isLoading, inviteAccess.allowed])
}
